<template>
  <el-form
    @submit.prevent="validateSubmit('catForm')"
    :model="category"
    :rules="rules"
    label-position="top"
    ref="catForm"
    novalidate
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
            :title="$t('actions.saveChanges')"
            isSuccess
            faIcon="save"
            @click.prevent="validateSubmit('catForm')"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
            :title="$t('actions.clearButton')"
            isDanger
            faIcon="trash"
            @click.prevent="clearForm('catForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('section') }}</h1>

      <el-form-item :label="t('name')" prop="name" class="input-label">
        <el-input
          id="name"
          v-model="category.name"
          :placeholder="t('namePlaceholder')"
          maxlength="255"
          show-word-limit
          clearable
          required
        >
        </el-input>
      </el-form-item>

      <el-form-item :label="t('parentCategory')" class="input-label">
        <el-tree-select
              v-model="category.parentID"
              popper-class="rtl"
              :data="categoriesTree"
              filterable
              check-strictly
              :render-after-expand="false"
              show-checkbox
              check-on-click-node
              style="width: 100%"
            />
      </el-form-item>
    </input-section>
  </el-form>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// components
// import Alert from "@/components/Alert.vue";
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import AlertBadge from '@/components/AlertBadge.vue'

import { ElForm } from 'element-plus'

// models
import Category, { categoryI18nMessages } from '@/models/inventory/Category'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'category-form',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...categoryI18nMessages.en,
          section: 'Category Info',
          namePlaceholder: 'For example, women clothing',
          skipParent: 'Skip'
        },
        ar: {
          ...categoryI18nMessages.ar,
          section: 'معلومات المجموعة',
          namePlaceholder: 'مثلا: ملابس نسائية',
          skipParent: 'تخطي'
        }
      },
      unescape: 'global'
    })

    return { t }
  },

  components: {
    AlertBadge,
    HorizontalButton,
    // CustomTextInput,
    InputSection
  },
  props: {
    initCategory: {
      type: Category,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },

  created() {
    // if (!this.category.parentCategory) {
    //   // create an empty parent
    //   this.category.parentCategory = new Category();
    // }
  },

  async beforeMount() {
    try {
      const catResp = await this.$http.get<Category[]>(Category.ENDPOINT)
      this.categories = catResp.map((cat) => Category.from(cat))
      this.buildCategoriesTree()

    } catch (error) {
      this.error.title = error?.title
      this.error.body = error?.body
    }
  },

  data() {
    return {
      category: this.initCategory,
      categories: new Array<Category>(),
      error: { title: '', body: '' },
      categoriesTree: new Array<any>(),
      rules: {
        name: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          },
          {
            max: 255,
            message: this.$t('validation.maxLength', { len: 255 }),
            trigger: 'blur'
          }
        ]
      }
    }
  },

  methods: {
    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        }

        const resp = await this.submitAction(this.category)

        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        } else {
          if (this.clearFormOnSubmit) {
            this.clearForm(formName)
          }

          this.$alertModal.showSuccess({ title: this.successMsg, body: '' })
        }
        return true
      })
    },
    buildCategoriesTree() {
      interface TreeNode {
        value: string
        label: string
        children?: TreeNode[]
      }
      
      const categoryMap = new Map<string, TreeNode>()
      
      // First, create nodes for all categories
      this.categories.forEach(category => {
        categoryMap.set(category.id, {
          value: category.id,
          label: category.name,
          children: []
        })
      })
      
      // Build the tree structure
      const rootCategories: TreeNode[] = []
      
      this.categories.forEach(category => {
        const node = categoryMap.get(category.id)
        
        if (category.parentID && categoryMap.has(category.parentID)) {
          // This category has a parent, add it to parent's children
          const parent = categoryMap.get(category.parentID)
          if (parent && node) {
            parent.children!.push(node)
          }
        } else {
          // This is a root category (no parent or parent doesn't exist)
          if (node) {
            rootCategories.push(node)
          }
        }
      })
      
      const cleanEmptyChildren = (node: TreeNode): void => {
        if (node.children && node.children.length === 0) {
          delete node.children
        } else if (node.children) {
          node.children.forEach(cleanEmptyChildren)
        }
      }
      
      rootCategories.forEach(cleanEmptyChildren)
      
      this.categoriesTree = rootCategories
    }
  }
})
</script>

<style scoped>
h1,
h2,
h3 {
  @apply text-lg  font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
